'use client';

import Image from 'next/image';
import {
    dataLayerPush,
    getBaseUrlJoined,
    layoutEvents,
    trackCheckoutSignUpImpression,
} from '@libs/analytics';
import {
    AccountDrawerType,
    LegacyIcon,
    LegacyIconName,
    clsx,
    isSSR,
    useIsExperimentFullSolution,
    usePageLayout,
} from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';
import {
    Button,
    Dropdown,
    LogoTreatsRewards,
    TextLink,
} from '@petsmart-ui/sparky';

import './SignIn.scss';
import { useEffect, useState } from 'react';

export const trackingData = (data: object) =>
    Object.assign(data, { ...layoutEvents.headerClick });

const IMG_SIZE_SIGNIN_LOYALTY = {
    width: 130,
    height: 26,
};

export function SignIn() {
    const [isCartPage, setIsCartPage] = useState(false);
    const { flags, openAccountDrawer } = usePageLayout();
    const isExperimentFullSolution = useIsExperimentFullSolution();

    const wrapperClasses = clsx(
        'legacy-signin',
        flags.loyaltyAccountMenu ? 'loyalty-account-menu-enabled' : ''
    );
    const currentPage = isSSR() ? '' : new URL(window.location.href).pathname;

    useEffect(() => {
        setIsCartPage(['/checkout-login', '/cart'].includes(currentPage));
    }, [currentPage]);

    const redirectURL = isCartPage
        ? `${NAVIGATION_URLS.velocity.createAccount}?redirectUrl=${currentPage}`
        : NAVIGATION_URLS.velocity.createAccount;

    const onSignInClick = () => {
        if (isExperimentFullSolution) {
            trackCheckoutSignUpImpression();
            openAccountDrawer(AccountDrawerType.SIGN_IN);
        }
    };

    return (
        <div className={wrapperClasses} data-testid="sign-in">
            <div className="legacy-signin__button">
                <LegacyIcon
                    className="legacy-signin__icon"
                    name={LegacyIconName.TREATS_NEW_LG}
                    priority
                    size={45}
                />
                <div className="legacy-signin__copy">
                    <TextLink
                        className="legacy-signin__link"
                        href={
                            isExperimentFullSolution
                                ? NAVIGATION_URLS.velocity.signIn
                                : NAVIGATION_URLS.legacy.account.base
                        }
                        onClick={() =>
                            dataLayerPush(
                                trackingData({
                                    selection: 'login',
                                    link_url: getBaseUrlJoined(
                                        NAVIGATION_URLS.legacy.account.base
                                    ),
                                })
                            )
                        }
                    >
                        <span>Sign In</span>
                        <span className="legacy-account__caret" />
                        <br />
                        Rewards &amp; Account
                    </TextLink>
                </div>
            </div>

            <Dropdown className="legacy-signin__dropdown">
                <section className="signin-dropcontent">
                    <div className="signin-dropcontent__container">
                        {flags.loyaltyAccountMenu ? (
                            <LogoTreatsRewards />
                        ) : (
                            <Image
                                src="https://s7d2.scene7.com/is/image/PetSmart/LY_2_SignInAccountHover_treatslogo_allsize_0611?UT0501L$"
                                alt="Loyalty Logo"
                                className="signin-dropcontent__logo"
                                {...IMG_SIZE_SIGNIN_LOYALTY}
                                unoptimized
                            />
                        )}

                        <div className="signin-dropcontent__content">
                            <p>
                                Join our
                                <TextLink
                                    className="signin-dropcontent__loyalty"
                                    href="/treats-rewards"
                                >
                                    loyalty program
                                </TextLink>
                                &amp; earn points every time you shop!
                            </p>
                            <Button
                                type="button"
                                className="signin-dropcontent__button"
                                variant="primary"
                                fullWidth
                                text={
                                    flags.loyaltyAccountMenu
                                        ? 'Sign in'
                                        : 'Login'
                                }
                                onClick={onSignInClick}
                                href={
                                    !isExperimentFullSolution
                                        ? NAVIGATION_URLS.legacy.account.base
                                        : undefined
                                }
                            />
                            <TextLink
                                className="signin-dropcontent__textlink"
                                href={
                                    isExperimentFullSolution
                                        ? redirectURL
                                        : NAVIGATION_URLS.legacy.createAccount
                                }
                            >
                                {flags.loyaltyAccountMenu
                                    ? 'Create account'
                                    : 'Sign up'}
                            </TextLink>
                        </div>
                    </div>
                </section>
            </Dropdown>

            <div className="legacy-signin__accent" />
        </div>
    );
}
